import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, CalendarDays, Trash2, RotateCcw, History,
  MapPin, FileText, ListChecks, X, Archive, Link2,
} from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, addWeeks, addDays, format, isSameDay, isSameMonth, parseISO, isValid,
} from "date-fns";
import { api } from "@/lib/apiClient";
import { useOffline } from "@/context/OfflineContext";
import { useGate } from "@/context/GateContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const VIEWS = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
  { key: "agenda", label: "Agenda" },
];

const safeDate = (iso) => {
  try { const d = parseISO(iso); return isValid(d) ? d : null; } catch { return null; }
};
const fmtTime = (iso) => { const d = safeDate(iso); return d ? format(d, "h:mm a") : ""; };
const fmtDay = (iso) => { const d = safeDate(iso); return d ? format(d, "EEE, MMM d, yyyy") : ""; };

function EventChip({ ev, onClick }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      data-testid={`cal-event-${ev.id}`}
      className="w-full text-left truncate px-1.5 py-0.5 rounded text-[10px] font-medium transition-transform hover:-translate-y-px"
      style={{ background: `${ev.color}22`, color: ev.color, border: `1px solid ${ev.color}55` }}
      title={ev.title}
    >
      {ev._pending && <span data-testid="pending-badge" title="Saved offline">&#9203; </span>}{!ev.all_day && <span className="opacity-80">{fmtTime(ev.starts_at)} </span>}{ev.title}
    </button>
  );
}

export default function EvidenceCalendar({ evidence = [], incidents = [], matterId, reloadIntegrity, refreshSignal = 0 }) {
  const { submit, pending, onSynced } = useOffline();
  const { gate, isGuest } = useGate();
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState(typeof window !== "undefined" && window.innerWidth < 640 ? "agenda" : "month");
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null); // event object or {} for new
  const [detail, setDetail] = useState(null);
  const [historyEv, setHistoryEv] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/events", { params: matterId ? { matter_id: matterId } : {} });
      setEvents(data);
    } catch (e) { if (!isGuest && e?.response?.status !== 401) toast.error("Could not load calendar"); }
  }, [matterId, isGuest]);

  useEffect(() => { load(); api.get("/calendar/event-types").then(({ data }) => setTypes(data)).catch((e) => console.error("load event-types failed", e)); }, [load]);
  useEffect(() => onSynced(() => load()), [onSynced, load]);
  useEffect(() => { if (refreshSignal) load(); }, [refreshSignal, load]);

  const loadArchived = async () => {
    try { const { data } = await api.get("/calendar/events/archived", { params: matterId ? { matter_id: matterId } : {} }); setArchived(data); }
    catch { toast.error("Could not load archived events"); }
  };

  const allEvents = useMemo(() => {
    const pend = (pending || [])
      .filter((p) => p.kind === "calendar" && (p.payload.matter_id || null) === (matterId || null))
      .map((p) => ({ id: "pending-" + p.id, title: p.payload.title, event_type: p.payload.event_type, type_label: (types.find((t) => t.key === p.payload.event_type) || {}).label || "Event", color: p.payload.color || "#94a3b8", starts_at: p.payload.starts_at, all_day: p.payload.all_day, notes: p.payload.notes, location: p.payload.location, evidence: [], incidents: [], _pending: true }));
    return [...events, ...pend];
  }, [events, pending, matterId, types]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of allEvents) {
      const d = safeDate(ev.starts_at);
      if (!d) continue;
      const key = format(d, "yyyy-MM-dd");
      (map[key] = map[key] || []).push(ev);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || "")));
    return map;
  }, [allEvents]);

  const shift = (dir) => {
    if (view === "month") setCursor((c) => addMonths(c, dir));
    else if (view === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addDays(c, dir));
  };

  const title = view === "month" ? format(cursor, "MMMM yyyy")
    : view === "week" ? `${format(startOfWeek(cursor), "MMM d")} – ${format(endOfWeek(cursor), "MMM d, yyyy")}`
    : view === "day" ? format(cursor, "EEEE, MMMM d, yyyy") : "Upcoming events";

  const afterSave = () => { load(); reloadIntegrity && reloadIntegrity(); };

  return (
    <div data-testid="calendar-view">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ek-mono-label mb-1">EVIDENCE CALENDAR</div>
          <h2 className="font-head text-2xl font-medium tracking-tight">Dates & deadlines</h2>
          <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>{events.length} event{events.length === 1 ? "" : "s"} · linked to evidence & records</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--ek-border)" }}>
            {VIEWS.map((v) => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`cal-view-${v.key}`}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={{ background: view === v.key ? "var(--ek-primary)" : "transparent", color: view === v.key ? "#fff" : "var(--ek-text-secondary)" }}>
                {v.label}
              </button>
            ))}
          </div>
          <Button onClick={() => { setShowArchived(true); loadArchived(); }} variant="outline" data-testid="cal-archived-btn" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }} title="Archived events"><Archive className="h-4 w-4" /></Button>
          <Button onClick={() => gate(() => setEditing({ starts_at: cursor.toISOString() }))} data-testid="cal-new-event-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}><Plus className="h-4 w-4 mr-1" /> New event</Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button onClick={() => shift(-1)} variant="outline" size="sm" data-testid="cal-prev" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}><ChevronLeft className="h-4 w-4" /></Button>
          <Button onClick={() => setCursor(new Date())} variant="outline" size="sm" data-testid="cal-today" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}>Today</Button>
          <Button onClick={() => shift(1)} variant="outline" size="sm" data-testid="cal-next" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="font-head font-semibold tracking-tight" data-testid="cal-title">{title}</div>
      </div>

      {view === "month" && <MonthView cursor={cursor} eventsByDay={eventsByDay} onDay={(d) => setEditing({ starts_at: d.toISOString() })} onEvent={setDetail} />}
      {view === "week" && <WeekView cursor={cursor} eventsByDay={eventsByDay} onDay={(d) => setEditing({ starts_at: d.toISOString() })} onEvent={setDetail} />}
      {view === "day" && <DayView cursor={cursor} eventsByDay={eventsByDay} onEvent={setDetail} onAdd={() => setEditing({ starts_at: cursor.toISOString() })} />}
      {view === "agenda" && <AgendaView events={allEvents} onEvent={setDetail} />}

      <EventDialog open={!!editing} event={editing} types={types} evidence={evidence} incidents={incidents} matterId={matterId} submit={submit}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); afterSave(); }} />
      <EventDetail event={detail} onClose={() => setDetail(null)}
        onEdit={(ev) => { setDetail(null); setEditing(ev); }}
        onDelete={async (ev) => { try { await api.delete(`/calendar/events/${ev.id}`); toast.success("Event archived"); setDetail(null); afterSave(); } catch { toast.error("Delete failed"); } }}
        onHistory={(ev) => { setDetail(null); setHistoryEv(ev); }} />
      <HistoryDialog event={historyEv} onClose={() => setHistoryEv(null)} />
      <ArchivedDialog open={showArchived} events={archived} onClose={() => setShowArchived(false)}
        onRestore={async (ev) => { try { await api.post(`/calendar/events/${ev.id}/restore`); toast.success("Event restored"); await loadArchived(); afterSave(); } catch { toast.error("Restore failed"); } }} />
    </div>
  );
}

function MonthView({ cursor, eventsByDay, onDay, onEvent }) {
  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)) });
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--ek-border)" }} data-testid="cal-month">
      <div className="grid grid-cols-7 border-b" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="ek-mono-label text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const evs = eventsByDay[key] || [];
          const inMonth = isSameMonth(d, cursor);
          const today = isSameDay(d, new Date());
          return (
            <div key={key} onClick={() => onDay(d)} data-testid={`cal-day-${key}`}
              className="min-h-[92px] border-b border-r p-1.5 cursor-pointer transition-colors hover:bg-[color:var(--ek-surface-hover)]"
              style={{ borderColor: "var(--ek-border)", background: inMonth ? "var(--ek-page)" : "rgba(148,163,184,0.03)", opacity: inMonth ? 1 : 0.5 }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium h-5 w-5 flex items-center justify-center rounded-full"
                  style={{ background: today ? "var(--ek-primary)" : "transparent", color: today ? "#fff" : "var(--ek-text-secondary)" }}>{format(d, "d")}</span>
              </div>
              <div className="space-y-1">
                {evs.slice(0, 3).map((ev) => <EventChip key={ev.id} ev={ev} onClick={onEvent} />)}
                {evs.length > 3 && <div className="ek-mono-label pl-1" style={{ color: "var(--ek-text-muted)" }}>+{evs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, eventsByDay, onDay, onEvent }) {
  const days = eachDayOfInterval({ start: startOfWeek(cursor), end: endOfWeek(cursor) });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="cal-week">
      {days.map((d) => {
        const key = format(d, "yyyy-MM-dd");
        const evs = eventsByDay[key] || [];
        const today = isSameDay(d, new Date());
        return (
          <div key={key} className="border rounded-md p-2 min-h-[140px]" style={{ borderColor: today ? "var(--ek-primary)" : "var(--ek-border)", background: "var(--ek-page)" }} data-testid={`cal-week-day-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="ek-mono-label">{format(d, "EEE d")}</div>
              <button onClick={() => onDay(d)} className="text-slate-500 hover:text-slate-200"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            <div className="space-y-1">
              {evs.length === 0 ? <div className="text-[10px]" style={{ color: "var(--ek-text-muted)" }}>—</div> : evs.map((ev) => <EventChip key={ev.id} ev={ev} onClick={onEvent} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ cursor, eventsByDay, onEvent, onAdd }) {
  const key = format(cursor, "yyyy-MM-dd");
  const evs = eventsByDay[key] || [];
  return (
    <div className="border rounded-lg p-4" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }} data-testid="cal-day">
      {evs.length === 0 ? (
        <div className="py-10 text-center">
          <CalendarDays className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.2} />
          <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>No events on this day.</p>
          <button onClick={onAdd} data-testid="cal-day-add" className="text-sm mt-2" style={{ color: "var(--ek-primary)" }}>Add an event →</button>
        </div>
      ) : (
        <div className="space-y-2">
          {evs.map((ev) => (
            <button key={ev.id} onClick={() => onEvent(ev)} data-testid={`cal-event-${ev.id}`} className="w-full flex items-center gap-3 border rounded-md p-3 text-left transition-colors hover:bg-[color:var(--ek-surface-hover)]" style={{ borderColor: "var(--ek-border)" }}>
              <span className="h-8 w-1 rounded-full" style={{ background: ev.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{ev.title}</div>
                <div className="ek-mono-label">{ev.all_day ? "ALL DAY" : fmtTime(ev.starts_at)} · {ev.type_label}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AgendaView({ events, onEvent }) {
  const upcoming = [...events].sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
  if (!upcoming.length) return <div className="border border-dashed rounded-lg p-16 text-center" style={{ borderColor: "var(--ek-border)" }} data-testid="cal-agenda-empty"><p style={{ color: "var(--ek-text-secondary)" }}>No events scheduled.</p></div>;
  return (
    <div className="space-y-2" data-testid="cal-agenda">
      {upcoming.map((ev) => (
        <button key={ev.id} onClick={() => onEvent(ev)} data-testid={`cal-event-${ev.id}`} className="w-full flex items-center gap-3 border rounded-md p-3 text-left transition-colors hover:bg-[color:var(--ek-surface-hover)]" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }}>
          <span className="h-10 w-1.5 rounded-full" style={{ background: ev.color }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{ev.title}</div>
            <div className="ek-mono-label">{fmtDay(ev.starts_at)} · {ev.all_day ? "ALL DAY" : fmtTime(ev.starts_at)} · {ev.type_label}</div>
          </div>
          {(ev.evidence?.length > 0 || ev.incidents?.length > 0) && <Link2 className="h-4 w-4 shrink-0" style={{ color: "var(--ek-primary)" }} />}
        </button>
      ))}
    </div>
  );
}

function EventDialog({ open, event, types, evidence, incidents, matterId, submit, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { gate } = useGate();

  useEffect(() => {
    if (!event) { setForm(null); return; }
    const d = safeDate(event.starts_at) || new Date();
    const end = safeDate(event.ends_at);
    setForm({
      id: event.id || null,
      title: event.title || "",
      event_type: event.event_type || "court",
      color: event.color || (types.find((t) => t.key === (event.event_type || "court"))?.color) || "#2E82F6",
      date: format(d, "yyyy-MM-dd"),
      time: event.all_day ? "" : format(d, "HH:mm"),
      end_time: end ? format(end, "HH:mm") : "",
      all_day: !!event.all_day,
      location: event.location || "",
      notes: event.notes || "",
      evidence_ids: event.evidence_ids || (event.evidence || []).map((e) => e.id) || [],
      incident_ids: event.incident_ids || (event.incidents || []).map((i) => i.id) || [],
    });
  }, [event, types]);

  if (!open || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickType = (key) => { const t = types.find((x) => x.key === key); set("event_type", key); if (t) set("color", t.color); };
  const toggle = (k, id) => setForm((f) => ({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] }));

  const save = async () => {
    if (!form.title.trim()) { toast.message("Add a title"); return; }
    if (!gate()) return;
    setSaving(true);
    const startISO = new Date(`${form.date}T${form.all_day ? "00:00" : (form.time || "00:00")}`).toISOString();
    const endISO = !form.all_day && form.end_time ? new Date(`${form.date}T${form.end_time}`).toISOString() : null;
    const payload = {
      title: form.title.trim(), event_type: form.event_type, color: form.color,
      starts_at: startISO, ends_at: endISO, all_day: form.all_day,
      location: form.location.trim(), notes: form.notes.trim(),
      evidence_ids: form.evidence_ids, incident_ids: form.incident_ids, matter_id: matterId,
    };
    try {
      if (form.id) {
        await api.patch(`/calendar/events/${form.id}`, payload);
        toast.success("Event updated");
        onSaved();
      } else {
        const res = await submit({ kind: "calendar", payload });
        if (res.ok === false) throw (res.error || new Error("save failed"));
        if (res.queued) { toast.message("Saved offline — will sync when you're back online"); onClose(); }
        else { toast.success("Event added to calendar"); onSaved(); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save event"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-auto sm:max-w-lg" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><CalendarDays className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> {form.id ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Court dates, exchanges and deadlines — link the evidence and records that support them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="ek-mono-label mb-1 block">TITLE</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="event-title-input" className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ek-mono-label mb-1 block">TYPE</label>
              <select value={form.event_type} onChange={(e) => pickType(e.target.value)} data-testid="event-type-select" className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }}>
                {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="ek-mono-label mb-1 block">COLOR</label>
              <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="event-color-input" className="w-full h-9 rounded-md cursor-pointer" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)" }} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="ek-mono-label mb-1 block">DATE</label>
              <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="event-date-input" className="w-full rounded-md px-2 py-2 text-sm" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
            </div>
            <div>
              <label className="ek-mono-label mb-1 block">START</label>
              <input type="time" value={form.time} disabled={form.all_day} onChange={(e) => set("time", e.target.value)} data-testid="event-time-input" className="w-full rounded-md px-2 py-2 text-sm disabled:opacity-40" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
            </div>
            <div>
              <label className="ek-mono-label mb-1 block">END</label>
              <input type="time" value={form.end_time} disabled={form.all_day} onChange={(e) => set("end_time", e.target.value)} data-testid="event-end-input" className="w-full rounded-md px-2 py-2 text-sm disabled:opacity-40" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.all_day} onChange={(e) => set("all_day", e.target.checked)} data-testid="event-allday-input" /> All-day event
          </label>
          <div>
            <label className="ek-mono-label mb-1 block flex items-center gap-1"><MapPin className="h-3 w-3" /> LOCATION</label>
            <input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="event-location-input" className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
          </div>
          <div>
            <label className="ek-mono-label mb-1 block flex items-center gap-1"><FileText className="h-3 w-3" /> NOTES</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="event-notes-input" className="w-full rounded-md px-3 py-2 text-sm resize-none" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)", color: "var(--ek-text)" }} />
          </div>

          <div>
            <label className="ek-mono-label mb-1 block flex items-center gap-1"><Link2 className="h-3 w-3" /> LINK EVIDENCE ({form.evidence_ids.length})</label>
            <div className="max-h-28 overflow-auto border rounded-md p-2 space-y-1" style={{ borderColor: "var(--ek-border)" }} data-testid="event-evidence-list">
              {evidence.length === 0 && <div className="text-xs" style={{ color: "var(--ek-text-muted)" }}>No evidence in this case yet.</div>}
              {evidence.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`event-link-evidence-${e.id}`}>
                  <input type="checkbox" checked={form.evidence_ids.includes(e.id)} onChange={() => toggle("evidence_ids", e.id)} />
                  <span className="truncate" data-private>{e.filename}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="ek-mono-label mb-1 block flex items-center gap-1"><ListChecks className="h-3 w-3" /> LINK TIMELINE RECORDS ({form.incident_ids.length})</label>
            <div className="max-h-28 overflow-auto border rounded-md p-2 space-y-1" style={{ borderColor: "var(--ek-border)" }} data-testid="event-incident-list">
              {incidents.length === 0 && <div className="text-xs" style={{ color: "var(--ek-text-muted)" }}>No timeline records in this case yet.</div>}
              {incidents.map((i) => (
                <label key={i.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`event-link-incident-${i.id}`}>
                  <input type="checkbox" checked={form.incident_ids.includes(i.id)} onChange={() => toggle("incident_ids", i.id)} />
                  <span className="truncate" data-private>{(i.pure_fact || "").slice(0, 60) || "Record"}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="event-save-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>{saving ? "Saving…" : (form.id ? "Save changes" : "Add event")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventDetail({ event, onClose, onEdit, onDelete, onHistory }) {
  if (!event) return null;
  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="event-detail">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2">
            <span className="h-4 w-4 rounded-full" style={{ background: event.color }} /> {event.title}
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>{event.type_label} · {event.all_day ? "All day" : fmtTime(event.starts_at)} · {fmtDay(event.starts_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {event.location && <div className="flex items-center gap-2" style={{ color: "var(--ek-text-secondary)" }}><MapPin className="h-4 w-4" /> {event.location}</div>}
          {event.notes && <p style={{ color: "var(--ek-text)" }} data-private>{event.notes}</p>}
          {event.evidence?.length > 0 && (
            <div>
              <div className="ek-mono-label mb-1">LINKED EVIDENCE</div>
              {event.evidence.map((e) => <div key={e.id} className="text-xs flex items-center gap-2" data-private><FileText className="h-3 w-3" style={{ color: "var(--ek-primary)" }} /> {e.filename}</div>)}
            </div>
          )}
          {event.incidents?.length > 0 && (
            <div>
              <div className="ek-mono-label mb-1">LINKED RECORDS</div>
              {event.incidents.map((i) => <div key={i.id} className="text-xs flex items-center gap-2" data-private><ListChecks className="h-3 w-3" style={{ color: "var(--ek-primary)" }} /> {i.pure_fact}</div>)}
            </div>
          )}
          <div className="ek-mono-label" style={{ color: "var(--ek-text-muted)" }}>Version {event.version || 1}</div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {event._pending ? (
            <span className="ek-mono-label" style={{ color: "var(--ek-warning)" }}>PENDING SYNC — saves when you're back online</span>
          ) : (
            <>
              <Button onClick={() => onHistory(event)} variant="outline" data-testid="event-history-btn" className="rounded-md border" style={{ borderColor: "var(--ek-border)" }}><History className="h-4 w-4 mr-1" /> History</Button>
              <Button onClick={() => onDelete(event)} variant="outline" data-testid="event-delete-btn" className="rounded-md border" style={{ borderColor: "rgba(240,97,109,0.4)", color: "var(--ek-error)" }}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
              <Button onClick={() => onEdit(event)} data-testid="event-edit-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>Edit</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ event, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    if (event) api.get(`/calendar/events/${event.id}/history`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [event]);
  if (!event) return null;
  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="event-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><History className="h-5 w-5" style={{ color: "#E4B34A" }} /> Event history</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>{event.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-80 overflow-auto">
          {items === null && <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>Loading…</p>}
          {items?.length === 0 && <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>No history recorded.</p>}
          {items?.map((e, i) => (
            <div key={i} className="flex gap-3">
              <div className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: "var(--ek-primary)" }} />
              <div>
                <div className="text-sm font-medium">{e.action}{e.detail ? ` — ${e.detail}` : ""}</div>
                <div className="ek-mono-label">{fmtDay(e.at)}{e.version ? ` · v${e.version}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedDialog({ open, events, onClose, onRestore }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="cal-archived-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><Archive className="h-5 w-5" style={{ color: "var(--ek-text-muted)" }} /> Archived events</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-auto">
          {(!events || events.length === 0) && <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>No archived events.</p>}
          {events?.map((ev) => (
            <div key={ev.id} className="flex items-center justify-between gap-2 border rounded-md p-2" style={{ borderColor: "var(--ek-border)" }}>
              <div className="min-w-0">
                <div className="text-sm truncate">{ev.title}</div>
                <div className="ek-mono-label">{fmtDay(ev.starts_at)}</div>
              </div>
              <button onClick={() => onRestore(ev)} data-testid={`cal-restore-${ev.id}`} className="text-slate-500 hover:text-emerald-400" title="Restore"><RotateCcw className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
